import { useState, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import routes from "@/routes";
import dayjs from "dayjs";
import {
  Input,
  Form,
  Modal,
  Typography,
  Row,
  Col,
  Checkbox,
  notification,
  Button,
  Flex,
  Spin,
  Skeleton,
  Space,
  Grid,
} from "antd";

import { UserContext } from "@/context/UserContext";
import MainContext from "@/layouts/MainLayout/context";

import { useTaskPermissions } from "@/hooks/useTaskPermissions";
import { useTaskTemplate } from "@/hooks/useTaskTemplate";
import { getTaskInitialValues } from "@/hooks/useTaskInitialValues";
import { useUnitSprints } from "@/hooks/useUnitSprints";

import TrashIcon from "@/icons/TrashIcon";
import RefreshIcon from "@/icons/RefreshIcon";

import AssignTo from "@/components/ui/AssignTo";
import CopyFrom from "@/components/ui/CopyFrom";
import {
  MarkdownEditor,
  normalizeDescriptionMarkdown,
} from "@/components/ui/MarkdownText";
import CustomSelect from "@/components/inputs/CustomSelect";
import DigitsDatePicker from "@/components/inputs/DigitsDatePicker";

import PublishModal from "@/components/modals/PublishModal";
import RepeatModal from "@/components/modals/RepeatModal";
import MapModal from "@/components/modals/MapModal";
// import PlannedLaborCostsModal from "@/components/modals/PlannedLaborCostsModal";
import ActualLaborCostsModal from "@/components/modals/ActualLaborCostsModal";

import { getRepeatText } from "@/utils/repeatText";
import {
  renderPriorityTag,
  renderPriorityOption,
  renderStatusTag,
  renderStatusOption,
  renderTaskTypeOption,
  renderTaskTypeTag,
} from "@/utils/tasks";

import {
  createItem,
  updateItem,
  getTasksOptions,
  getItem as getTask,
} from "@/api/tasks";
import { getUnitEmployees } from "@/api/employees";
import {
  getTemplatesOptions,
  createItem as createTemplate,
  getRepeatRules,
} from "@/api/task-templates";
import { getLocationText } from "@/api/map";
import { isTaskStatusFinal } from "@/constants/tasks";
// import { getPluralizedWord } from "@/utils";

const { Text } = Typography;
const { useBreakpoint } = Grid;

// Helpers
const toLocationPayload = (loc) =>
  loc && loc.latitude && loc.longitude
    ? { x: loc.latitude, y: loc.longitude }
    : null;

const mapMembersToOptions = (members) => {
  return members.map((item) => {
    const { lastname, firstname, patronymic } = item;
    return {
      ...item,
      label: `${lastname || ""} ${firstname || ""} ${patronymic || ""}`,
    };
  });
};

// const toFloorNumber = (value, precision = 0) => {
//   if (value === null || value === undefined || value === "") return null;

//   const num = Number(String(value).replace(",", "."));
//   if (!Number.isFinite(num)) return null;

//   if (precision <= 0) {
//     return Math.floor(num);
//   }

//   const factor = 10 ** precision;
//   return Math.floor(num * factor) / factor;
// };

// Определяем пропсы для колонки с исполнителем в зависимости от условий
const getExecutorColProps = (task, draftStatus) => {
  const condition = task?.status_uuid !== draftStatus?.uuid;

  return {
    xs: { span: 24, pull: 0 },
    sm: {
      span: condition ? 16 : 24,
      pull: condition ? 8 : 0,
    },
  };
};

// Определяем пропсы для колонки с приоритетом в зависимости от условий
const getPriorityColProps = (task) => {
  const condition = !task?.uuid;

  return {
    xs: { span: condition ? 24 : 12, pull: 0 },
    sm: {
      span: 8,
      push: condition ? 16 : 0,
    },
  };
};

// Окно для создания/редактирования задачи, содержит форму
const TaskModal = ({
  isAdmins = false,
  data,
  template = null,
  statuses = [],
  priorities = [],
  units = [],
  taskTypes = [],
  visible,
  onSave,
  onCancel,
  unit_uuid,
  canEditUnit = true,
}) => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  const { config } = useContext(MainContext);
  const sprintName = config?.entities?.sprint?.name?.singular || "Спринт";

  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Опции для выпадающих списков
  const [authorsOptions, setAuthorsOptions] = useState(
    data?.author
      ? [
          {
            ...data.author,
            label: `${data.author.lastname || ""} ${
              data.author.firstname || ""
            } ${data.author.patronymic || ""}`,
          },
        ]
      : [],
  );
  const [executorsOptions, setExecutorOptions] = useState(
    data?.executor
      ? [
          {
            ...data.executor,
            label: `${data.executor.lastname || ""} ${
              data.executor.firstname || ""
            } ${data.executor.patronymic || ""}`,
          },
        ]
      : [],
  );
  const [coexecutorsOptions, setCoexecutorsOptions] = useState(
    data?.coexecutors
      ? [
          ...data.coexecutors.map((item) => ({
            ...item,
            label: `${item.lastname || ""} ${item.firstname || ""} ${
              item.patronymic || ""
            }`,
          })),
        ]
      : [],
  );
  const [acceptorsOptions, setAcceptorOptions] = useState(
    data?.assignee_acceptor
      ? [
          {
            ...data.assignee_acceptor,
            label: `${data.assignee_acceptor.lastname || ""} ${
              data.assignee_acceptor.firstname || ""
            } ${data.assignee_acceptor.patronymic || ""}`,
          },
        ]
      : [],
  );
  const [tasksOptions, setTasksOptions] = useState(
    data?.parent
      ? [
          {
            ...data.parent,
            label: `№${data.parent.task_number} - ${data.parent.name}`,
          },
        ]
      : [],
  );

  const [templatesOptions, setTemplatesOptions] = useState([]);
  const statusesOptions = useMemo(() => {
    return statuses.filter(({ is_for_draft }) => !is_for_draft);
  }, [statuses]);
  const prioritiesOptions = useMemo(() => {
    return priorities.filter(({ deleted }) => !deleted);
  }, [priorities]);
  const taskTypesOptions = useMemo(() => {
    return taskTypes.filter(({ deleted }) => !deleted);
  }, [taskTypes]);

  // Выбранный шаблон
  const [selectedTemplateUuid, setSelectedTemplateUuid] = useState(
    template?.uuid || null,
  );

  // Локальные флаги исполнителя и приёмщика
  const [isExecutor, setIsExecutor] = useState(
    user?.employee_uuid === data?.responsible_executor_uuid,
  );
  const [isAcceptor, setIsAcceptor] = useState(
    user?.employee_uuid === data?.assignee_acceptor_uuid,
  );

  // Флаг, является ли родительская задача черновиком
  const [isParentDraft, setIsParentDraft] = useState(false);

  // Константные значения для статуса, приоритета и РГ
  const defaultStatus = statuses.find(({ is_default }) => is_default) || {};
  const draftStatus = statuses.find(({ is_for_draft }) => is_for_draft);
  const defaultPriority = priorities.find(({ is_default }) => is_default) || {};
  const defaultTaskType = taskTypes.find(({ is_default }) => is_default) || {};
  const defaultUnit = units[0];
  const todayDate = dayjs();

  // Окно публикации задачи (из черновика)
  const [isPublishModalShow, setIsPublishModalShow] = useState(false);
  const [publishTask, setPublishTask] = useState(null);

  // Правила повторения задачи + модальное окно
  const [showRepeatModal, setShowRepeatModal] = useState(false);
  const [tempRepeatRules, setTempRepeatRules] = useState(null);
  const [repeatRules, setRepeatRules] = useState(null);

  // Трудозатраты + модальное окно
  const [plannedLaborCosts, setPlannedLaborCosts] = useState(
    {
      planned_costs: Number(data?.planned_costs) || null,
      require_actuals_on_acceptance: data?.require_actuals_on_acceptance,
      do_not_exceed_children: data?.do_not_exceed_children,
    } || null,
  );
  // const [tempPlannedLaborCosts, setTempPlannedLaborCosts] = useState({
  //   planned_costs: Number(data?.planned_costs),
  //   require_actuals_on_acceptance: data?.require_actuals_on_acceptance,
  //   do_not_exceed_children: data?.do_not_exceed_children,
  // } || null);

  // const [showPlannedLaborCostsModal, setShowPlannedLaborCostsModal] = useState(false);
  const [hasActualCosts, setHasActualCosts] = useState(false);
  const [showActualLaborCostsModal, setShowActualLaborCostsModal] =
    useState(false);

  // не показываем черновики в списке родительских задач, если выбраны правила повторения
  const filteredTasksOptions = useMemo(() => {
    if (!repeatRules) return tasksOptions.filter(
      ({ uuid }) => uuid !== data?.uuid,
    );

    return tasksOptions.filter(
      ({ status_uuid, uuid }) => status_uuid !== draftStatus?.uuid && uuid !== data?.uuid,
    );
  }, [tasksOptions, repeatRules, draftStatus, data]);

  // Местоположение + окно с выбором на карте
  const [showMapModal, setShowMapModal] = useState(false);
  const [addressPending, setAddressPending] = useState(false);
  const [address, setAddress] = useState("");
  const [location, setLocation] = useState(
    data?.location
      ? { latitude: data.location.x, longitude: data.location.y }
      : null,
  );

  // Инициализационные значения формы (чистая функция)
  const initialValue = getTaskInitialValues(
    data,
    template,
    { defaultUnit, defaultStatus, defaultPriority, defaultTaskType, units },
    dayjs(),
  );

  const {
    isUserCanEditStatus,
    isUserCanEditName,
    isUserCanEditPriority,
    isUserCanEditTaskType,
    isUserCanEditDescription,
    isUserCanEditAuthor,
    isUserCanEditExecutor,
    isUserCanEditAcceptor,
    isUserCanEditStartDate,
    isUserCanEditDueDate,
    isUserCanEditParentTask,
    isUserCanEditCoexecutors,
    isUserCanEditSprint,
    showSetMeExecutor,
    showSetMeAcceptor,
  } = useTaskPermissions(data, {
    user,
    executorsOptions,
    acceptorsOptions,
    isExecutor,
    isAcceptor,
  });

  // Подписки на изменения полей формы
  const parentUuid = Form.useWatch("parent_task_uuid", form);
  const startDate = Form.useWatch("start_date", form);
  const dueDate = Form.useWatch("due_date", form);
  const unitUuid = Form.useWatch("unit_uuid", form);

  // Спринт
  const { sprintsOptions, isLoadingSprints } = useUnitSprints(
    unitUuid,
    data?.sprint || null,
  );

  // Трудозатраты
  const initialParentUuid = data?.parent_task_uuid || null;
  const parentFromData = data?.parent || null;
  const currentParentUuid = parentUuid ?? initialParentUuid;

  const currentParentTask = useMemo(() => {
    if (!currentParentUuid) return parentFromData;

    const fromOptions = tasksOptions.find(
      (task) => task.uuid === currentParentUuid,
    );

    if (fromOptions) return fromOptions;
    if (parentFromData?.uuid === currentParentUuid) return parentFromData;

    return parentFromData;
  }, [currentParentUuid, tasksOptions, parentFromData]);

  // const isExistingTask = !!data?.uuid;
  // const isSameAsOriginalParent =
  //   !!initialParentUuid && currentParentUuid === initialParentUuid;

  const parentDoNotExceedChildren = !!currentParentTask?.do_not_exceed_children;

  // const parentAvailableLimit =
  //   currentParentTask?.available_limit_for_children != null
  //     ? Number(currentParentTask.available_limit_for_children)
  //     : null;

  // let plannedUpLimit = null;
  // if (parentDoNotExceedChildren && parentAvailableLimit != null) {
  //   if (isExistingTask && isSameAsOriginalParent) {
  //     const previousPlanned = Number(data?.planned_costs || 0);
  //     plannedUpLimit = parentAvailableLimit + previousPlanned;
  //   } else {
  //     plannedUpLimit = parentAvailableLimit;
  //   }
  // }

  // const plannedLowLimit =
  //   isSameAsOriginalParent && data?.sum_children_planned_costs != null
  //     ? Number(data.sum_children_planned_costs)
  //     : null;

  // const parentHasNoAvailableLimit =
  //   parentDoNotExceedChildren && (parentAvailableLimit ?? 0) <= 0;

  // Показать кнопку копирования описания из родительской задачи
  const showCopyDescriptionFromParent = useMemo(() => {
    return Boolean(isUserCanEditDescription && parentUuid);
  }, [isUserCanEditDescription, parentUuid]);

  // При инициализации компонента запрашиваем список исполнителей
  useEffect(() => {
    fetchUserList(data?.unit_uuid || defaultUnit.uuid);
    fetchTaskList();
    fetchTaskTemplateList();

    // eslint-disable-next-line
  }, [defaultUnit, data]);

  // Если пришел шаблон, применяем
  useEffect(() => {
    if (!data?.uuid && selectedTemplateUuid && templatesOptions.length) {
      const tpl = templatesOptions.find((t) => t.uuid === selectedTemplateUuid);
      if (tpl) applyTemplate(tpl);
    }
    // eslint-disable-next-line
  }, [templatesOptions]);

  useEffect(() => {
    if (!data?.uuid && template) {
      setSelectedTemplateUuid(template.uuid);
      applyTemplate(template);
    }
    // eslint-disable-next-line
  }, [template]);

  useEffect(() => {
    if (!location?.latitude || !location?.longitude) {
      setAddress("");
      return;
    }
    setAddressPending(true);

    getLocationText(location).then((text) => {
      setAddress(text);
      setAddressPending(false);
    });
  }, [location]);

  const fetchUserList = async (unit_uuid) => {
    try {
      const { members } = await getUnitEmployees({
        ...(unit_uuid && { unit_uuid }),
      });

      // Fixme: Может уже придумаем другой способ как обходить удаленных из РГ сотрудников?
      const authors = [...members];
      const executors = [...members];
      const acceptors = [...members];
      const coexecutors = [...members];

      if (
        data?.author &&
        !authors.find((item) => item.uuid === data.author.uuid)
      ) {
        authors.push(data.author);
      }

      if (
        data?.assignee_acceptor &&
        !acceptors.find((item) => item.uuid === data.assignee_acceptor.uuid)
      ) {
        acceptors.push(data.assignee_acceptor);
      }

      if (
        data?.executor &&
        !executors.find((item) => item.uuid === data.executor.uuid)
      ) {
        executors.push(data.executor);
      }

      if (data?.coexecutors?.length) {
        data.coexecutors.forEach((coexecutor) => {
          if (!coexecutors.find((member) => member.uuid === coexecutor.uuid)) {
            coexecutors.push(coexecutor);
          }
        });
      }

      setAuthorsOptions(mapMembersToOptions(authors));
      setExecutorOptions(mapMembersToOptions(executors));
      setAcceptorOptions(mapMembersToOptions(acceptors));
      setCoexecutorsOptions(mapMembersToOptions(coexecutors));
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTaskList = async () => {
    try {
      const { tasks } = await getTasksOptions({}, isAdmins);

      // Если пришла родительская задача, но ее нет в списке, добавляем из поля parent в котором пришли минимальные данные
      const hasHiddenParent =
        data?.parent_task_uuid &&
        !tasks.find((item) => item.uuid === data.parent_task_uuid);

      const parent = data?.parent;

      setTasksOptions([
        ...(hasHiddenParent
          ? [
              {
                ...parent,
                label: `№${parent.task_number} -  ${parent.name}`,
              },
            ]
          : []),
        ...tasks
          .filter(({ status }) => {
            const rules = !isTaskStatusFinal(status);
            return !data?.uuid || data?.status_uuid === draftStatus?.uuid
              ? rules
              : rules && !status.is_for_draft;
          })
          .map((item) => ({
            ...item,
            label: `№${item.task_number} -  ${item.name}`,
          }))
          .sort((a, b) => b.task_number - a.task_number), // сортируем по убыванию номеров задач
      ]);

      const parentTask = tasks.find(
        (task) => task.uuid === data?.parent_task_uuid,
      );

      setIsParentDraft(parentTask?.status?.is_for_draft || false);
    } catch (error) {
      console.log(error);

      if (data?.parent) {
        setTasksOptions([
          {
            ...data.parent,
            label: `№${data.parent.task_number} -  ${data.parent.name}`,
          },
        ]);
      } else {
        form.setFieldValue("parent_task_uuid", null);
        setTasksOptions([]);
      }
    }
  };

  const fetchTaskTemplateList = async () => {
    try {
      const { templates } = await getTemplatesOptions(
        { unit_uuid: unit_uuid },
        isAdmins,
      );

      setTemplatesOptions([
        ...templates
          .map((item) => ({
            ...item,
            label: (
              <Flex align="flex-start" gap={5}>
                {item.is_recurring_template && (
                  <RefreshIcon
                    style={{
                      fontSize: 14,
                      color: "#BBBBBB",
                      flexShrink: 0,
                      marginTop: 2,
                    }}
                  />
                )}
                <span
                  style={{
                    minWidth: 0,
                    lineHeight: "20px",
                    whiteSpace: "normal",
                    overflowWrap: "anywhere",
                  }}
                >
                  №{item.template_code} - {item.template_title}
                </span>
              </Flex>
            ),
          }))
          .sort((a, b) => b.template_code - a.template_code), // сортируем по убыванию
      ]);
    } catch (error) {
      console.log(error);
      form.setFieldValue("template_uuid", null);
    }
  };

  const fetchRepeatRules = async (template_uuid) => {
    try {
      const res = await getRepeatRules(template_uuid);
      setRepeatRules({
        ...res,
        ...(res.start_date && { start_date: dayjs(res.start_date) }),
        ...(res.end_date && { end_date: dayjs(res.end_date) }),
      });
    } catch (error) {
      console.log(error);
    }
  };

  const fetchLaborCostsRules = (costs) => {
    setPlannedLaborCosts({
      planned_costs: costs.planned_costs,
      require_actuals_on_acceptance: costs.require_actuals_on_acceptance,
      do_not_exceed_children: costs.do_not_exceed_children,
    });
  };

  // Хук применения шаблонов и чистая генерация initial values
  const { applyTemplate } = useTaskTemplate({
    form,
    user,
    fetchUserList,
    fetchRepeatRules,
    fetchLaborCostsRules,
    onSetIsExecutor: setIsExecutor,
    onSetIsAcceptor: setIsAcceptor,
    defaults: { defaultUnit, defaultStatus, defaultPriority, todayDate, units },
  });

  const setMeExecutor = async () => {
    setIsExecutor(true);

    form.setFieldsValue({
      responsible_executor_uuid: user.employee_uuid,
    });
  };
  const setMeAcceptor = async () => {
    setIsAcceptor(true);

    form.setFieldsValue({
      assignee_acceptor_uuid: user.employee_uuid,
    });
  };

  const setParentDescription = async () => {
    if (!parentUuid) return false;

    const res = await getTask(parentUuid);
    form.setFieldsValue({ description: res?.description ?? "" });

    return true;
  };

  const onValuesChange = async (changedValues) => {
    // Проверяем, изменилось ли РГ
    if ("unit_uuid" in changedValues) {
      setIsExecutor(false);
      setIsAcceptor(false);
      // сбрасываем исполнителя, приёмщика и соисполнителей
      form.setFieldsValue({
        responsible_executor_uuid: null,
        coexecutors_uuids: [],
        assignee_acceptor_uuid: null,
        sprint_uuid: null,
      });
      // Запрашиваем список новых
      fetchUserList(changedValues.unit_uuid);
    }

    if ("responsible_executor_uuid" in changedValues) {
      setIsExecutor(
        changedValues.responsible_executor_uuid === user.employee_uuid,
      );
    }

    if ("assignee_acceptor_uuid" in changedValues) {
      setIsAcceptor(
        changedValues.assignee_acceptor_uuid === user.employee_uuid,
      );
    }

    if ("parent_task_uuid" in changedValues) {
      const parentTask = tasksOptions.find(
        (task) => task.uuid === changedValues.parent_task_uuid,
      );

      setIsParentDraft(parentTask?.status?.is_for_draft || false);
    }

    if ("start_date" in changedValues) {
      // Если дату начала поменяли и она стала больше даты окончания, сбрасываем дату окончания
      if (dueDate && changedValues.start_date?.isAfter(dueDate)) {
        form.setFieldsValue({ due_date: null });
      }

      setTempRepeatRules(null);
    }

    if ("due_date" in changedValues) {
      setTempRepeatRules(null);
    }
  };

  const handleChangeDate = (fieldText, field, date, currentValue) => {
    Modal.confirm({
      title: `Вы уверены, что хотите изменить ${fieldText}?`,
      content: "Настроенные правила повторения будут сброшены.",
      onOk() {
        // Если пользователь согласился, обновляем дату и сбрасываем периодичность
        form.setFieldValue(field, date);
        setRepeatRules(null);
      },
      onCancel() {
        // Если пользователь отменил, возвращаем предыдущее значение
        form.setFieldValue(field, currentValue);
      },
      okText: "Да",
      cancelText: "Отмена",
    });
  };

  const onLaborCostSave = () => {
    setShowActualLaborCostsModal(false);
    setHasActualCosts(true);
  };

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    onCancel();
  };

  const handleSave = async (values) => {
    const currentStatus = statuses.find(
      (status) => status.uuid === values.status_uuid,
    );

    const isDraftSave = values.status_uuid === draftStatus?.uuid;
    const isPublishOrTaskSave = !isDraftSave;
    const hasParentLimit = parentDoNotExceedChildren;
    const hasNoPlannedCosts =
      plannedLaborCosts?.planned_costs == null ||
      Number(plannedLaborCosts.planned_costs) === 0;
    const resourcesPayload =
      Array.isArray(data?.resources) && data.resources.length
        ? {
            resources: data.resources.map((r) => ({
              object_uuid: r.object_uuid,
              definition_uuid: r.definition_uuid,
            })),
          }
        : {};

    setHasActualCosts(Number(data?.actual_costs || 0) > 0);

    if (
      data?.uuid &&
      plannedLaborCosts?.require_actuals_on_acceptance &&
      currentStatus.is_for_acceptance &&
      !hasActualCosts &&
      !(Number(data?.actual_costs || 0) > 0)
    ) {
      setShowActualLaborCostsModal(true);
      return;
    }

    setError(null);
    setIsLoading(true);

    const { is_create_new_template, ...rest } = values;

    const res = {
      ...rest,
      description: normalizeDescriptionMarkdown(rest.description),
      start_date: rest.start_date?.format().split("T")[0] || null,
      due_date: rest.due_date?.format() || null,
    };

    try {
      let newTask;
      if (data?.uuid) {
        newTask = await updateItem({
          ...data,
          ...res,
          // planned_costs: Number(plannedLaborCosts?.planned_costs) || null,
          // require_actuals_on_acceptance: plannedLaborCosts.require_actuals_on_acceptance,
          // do_not_exceed_children: plannedLaborCosts.do_not_exceed_children,
          location: toLocationPayload(location),
          ...resourcesPayload,
        });

        if (is_create_new_template) {
          // Собираем payload шаблона из полей задачи
          const tplPayload = {
            template_title: `Новый шаблон: ${
              newTask?.name || rest?.name || ""
            }`,
            // Поля содержимого
            name: newTask?.name ?? rest?.name ?? "",
            description: newTask?.description ?? res?.description ?? "",
            unit_uuid: newTask?.unit_uuid ?? rest?.unit_uuid ?? null,
            start_date: newTask?.start_date || res.start_date, // строки ISO/даты с сервера ок
            due_date: newTask?.due_date || res.due_date,
            responsible_executor_uuid:
              newTask?.responsible_executor_uuid ??
              rest?.responsible_executor_uuid ??
              null,
            assignee_acceptor_uuid:
              newTask?.assignee_acceptor_uuid ??
              rest?.assignee_acceptor_uuid ??
              null,
            importance_uuid:
              newTask?.importance_uuid ?? rest?.importance_uuid ?? null,
            coexecutors_uuids: Array.isArray(newTask?.coexecutors)
              ? newTask.coexecutors.map((c) => c.uuid)
              : (rest?.coexecutors_uuids ?? []),
            parent_task_uuid:
              newTask?.parent_task_uuid ?? rest?.parent_task_uuid ?? null,
            author_uuid:
              newTask?.author_uuid ??
              rest?.author_uuid ??
              user?.employee_uuid ??
              null,
            is_accessible_for_all_in_unit:
              typeof newTask?.is_accessible_for_all_in_unit === "boolean"
                ? newTask.is_accessible_for_all_in_unit
                : !!rest?.is_accessible_for_all_in_unit,
            is_visible:
              typeof newTask?.is_visible === "boolean"
                ? newTask.is_visible
                : true,
            // planned_costs: null,
            // require_actuals_on_acceptance: plannedLaborCosts.require_actuals_on_acceptance,
          };

          await createTemplate(tplPayload);
        }
      } else {
        newTask = await createItem({
          ...res,
          // Добавляем recurrence только если repeatRules существует
          ...(repeatRules && {
            recurrence: {
              ...repeatRules,
              start_date: repeatRules.start_date?.format().split("T")[0],
            },
          }),
          // planned_costs: Number(plannedLaborCosts?.planned_costs) || null,
          // require_actuals_on_acceptance: plannedLaborCosts.require_actuals_on_acceptance,
          // do_not_exceed_children: plannedLaborCosts.do_not_exceed_children,
          location: toLocationPayload(location),
          ...resourcesPayload,
        });

        notification.open({
          message: (
            <div style={{ fontWeight: 500 }}>
              Создана задача №{newTask.task_number} {newTask.name}
            </div>
          ),
          description: (
            <Button
              type="link"
              style={{
                background: "transparent",
                border: "1px solid #D5E2F7",
                color: "#0066a6",
                fontWeight: 500,
              }}
              onClick={() =>
                navigate(
                  isAdmins
                    ? routes.administration.taskDetail(newTask.uuid)
                    : unit_uuid
                      ? routes.unitTaskDetail(newTask.unit_uuid, newTask.uuid)
                      : routes.taskDetail(newTask.uuid),
                )
              }
            >
              Перейти к задаче
            </Button>
          ),
          icon: null,
          duration: 4,
          pauseOnHover: true,
          placement: "bottomRight",
          style: {
            border: "1px solid #B7EB8F",
            background: "#f6ffed",
            borderRadius: "8px",
            width: "500px",
          },
        });
      }

      if (isPublishOrTaskSave && hasParentLimit && hasNoPlannedCosts) {
        notification.warning({
          message: (
            <div style={{ fontWeight: 500 }}>
              Необходимо заполнить плановые трудозатраты
            </div>
          ),
          description: `У родительской задачи включен запрет на превышение суммарных плановых трудозатрат. Требуется указать плановые трудозатраты для задачи №${newTask.task_number} ${newTask.name}}`,
          placement: "bottomRight",
          duration: 6,
          style: {
            width: "500px",
          },
        });
      }

      onSave(newTask);
      form.resetFields();
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onOk = () => {
    form
      .validateFields()
      .then((values) => {
        // Если это редактирование обычной задачи
        if (values.status_uuid && values.status_uuid !== draftStatus?.uuid) {
          handleSave(values);
        } else {
          // Если это публикация черновика с подзадачами
          if (data?.has_children) {
            setPublishTask({ ...data, ...values });
            setIsPublishModalShow(true);
          } else {
            // Если это создание новой задачи (не черновика) или публикация черновика без подзадач
            handleSave({ ...values, status_uuid: defaultStatus?.uuid });
          }
        }
      })
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });
  };

  const onSaveDraft = () => {
    form
      .validateFields()
      .then((values) => {
        handleSave({ ...values, status_uuid: draftStatus?.uuid });
      })
      .catch((info) => {
        console.log("Ошибка валидации:", info);
      });
  };

  // Обработчик закрытия модалки публикации
  const handlePublishSave = (newTask) => {
    setIsPublishModalShow(false);
    onSave(newTask);
    form.resetFields();
  };

  return (
    <>
      <Modal
        maskClosable={false}
        onCancel={handleCancel}
        title={
          <Flex
            align={isMobile ? "stretch" : "center"}
            gap={8}
            vertical={isMobile}
            wrap="wrap"
          >
            <span>{data?.uuid ? "Редактирование задачи" : "Новая задача"}</span>

            {!data?.uuid && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: isMobile ? "100%" : undefined,
                }}
              >
                <CustomSelect
                  placeholder="Шаблоны"
                  options={templatesOptions}
                  showSearch
                  allowClear
                  value={selectedTemplateUuid || undefined}
                  fieldNames={{ value: "uuid" }}
                  optionFilterProp="label"
                  popupMatchSelectWidth={isMobile ? true : 411}
                  style={
                    isMobile
                      ? { width: "100%" }
                      : { minWidth: 100, maxWidth: 250 }
                  }
                  onChange={(tplUuid) => {
                    setSelectedTemplateUuid(tplUuid || null);
                    const tpl = templatesOptions.find(
                      (t) => t.uuid === tplUuid,
                    );
                    applyTemplate(tpl); // перезаполняем все поля
                  }}
                />
              </div>
            )}
          </Flex>
        }
        open={visible}
        width={600}
        footer={
          <Flex
            justify="space-between"
            vertical={isMobile}
            gap={isMobile ? 10 : 0}
          >
            <Button
              key="back"
              onClick={handleCancel}
              style={isMobile ? { width: "100%", whiteSpace: "normal" } : null}
            >
              Отмена
            </Button>

            <Flex
              gap={10}
              vertical={isMobile}
              style={isMobile ? { width: "100%" } : null}
            >
              {(!data?.uuid || data?.status_uuid === draftStatus?.uuid) && (
                <Button
                  key="save"
                  loading={isLoading}
                  onClick={onSaveDraft}
                  style={
                    isMobile ? { width: "100%", whiteSpace: "normal" } : null
                  }
                >
                  Сохранить черновик
                </Button>
              )}

              <Button
                key="submit"
                type="primary"
                loading={isLoading}
                onClick={onOk}
                disabled={isParentDraft}
                style={
                  isMobile ? { width: "100%", whiteSpace: "normal" } : null
                }
              >
                {data?.uuid && data?.status_uuid !== draftStatus?.uuid
                  ? "Сохранить"
                  : "Сохранить и опубликовать"}
              </Button>
            </Flex>
          </Flex>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValue}
          onFinish={handleSave}
          onValuesChange={onValuesChange}
        >
          <Form.Item name="template_uuid" hidden>
            <Input />
          </Form.Item>

          {data?.uuid && (
            <Form.Item
              name="author_uuid"
              label="Автор"
              rules={[
                { required: true, message: "Пожалуйста, заполните поле" },
              ]}
            >
              <CustomSelect
                placeholder="Начните вводить"
                options={authorsOptions}
                showSearch
                fieldNames={{ value: "uuid" }}
                optionFilterProp="label"
                disabled={!isUserCanEditAuthor}
              />
            </Form.Item>
          )}

          <Row gutter={{ xs: 10, sm: 10, md: 20, lg: 20 }}>
            <Col xs={24} sm={16}>
              <Form.Item label="Название" required>
                <Space.Compact className="w-100">
                  {data?.task_number && (
                    <Input
                      disabled
                      value={`№${data.task_number}`}
                      style={{
                        width: 80,
                        textAlign: "center",
                        backgroundColor: "#fafafa",
                        cursor: "default",
                        color: "#303030",
                      }}
                    />
                  )}

                  <Form.Item
                    name="name"
                    rules={[
                      {
                        required: true,
                        message: "Пожалуйста, введите название",
                      },
                    ]}
                    noStyle
                  >
                    <Input
                      disabled={!isUserCanEditName}
                      placeholder="Введите название"
                    />
                  </Form.Item>
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="task_type_uuid"
                label="Тип задачи"
                rules={[{ required: true, message: "Обязательное поле" }]}
              >
                <CustomSelect
                  showSearch
                  options={taskTypesOptions}
                  fieldNames={{ label: "name", value: "uuid" }}
                  optionFilterProp="name"
                  labelRender={renderTaskTypeTag(taskTypes)}
                  optionRender={renderTaskTypeOption}
                  disabled={!isUserCanEditTaskType}
                  popupMatchSelectWidth={false}
                  placement="bottomRight"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="parent_task_uuid" label="Родительская задача">
            <CustomSelect
              placeholder="Начните вводить"
              options={filteredTasksOptions}
              showSearch
              allowClear
              fieldNames={{ value: "uuid" }}
              optionFilterProp="label"
              disabled={!isUserCanEditParentTask}
              onChange={(value) => {
                form.setFieldsValue({
                  parent_task_uuid: value || null, // Устанавливаем null, если значение очищено
                });
              }}
            />
          </Form.Item>

          <CopyFrom
            showAction={showCopyDescriptionFromParent}
            onChange={setParentDescription}
            successText="Описание скопировано"
            text="Копировать из родительской задачи"
          >
            <Form.Item
              name="description"
              label="Описание"
              style={{ marginBottom: 8 }}
            >
              <MarkdownEditor rows={5} disabled={!isUserCanEditDescription} />
            </Form.Item>
          </CopyFrom>

          <div style={{ marginBottom: 24 }}>
            {location ? (
              <Spin spinning={addressPending}>
                <div
                  style={{
                    border: "1px solid var(--color-gray)",
                    borderRadius: 4,
                    padding: 12,
                    marginTop: 8,
                  }}
                >
                  <Flex vertical gap={12}>
                    <Flex vertical gap={6}>
                      <div style={{ color: "#777777" }}>Местоположение</div>

                      {address && !addressPending ? (
                        <div>{address}</div>
                      ) : (
                        <Skeleton.Input active size="small" block />
                      )}
                    </Flex>
                    <Flex gap={6}>
                      <Button onClick={() => setShowMapModal(true)}>
                        Изменить
                      </Button>

                      <Button
                        onClick={() => {
                          setLocation(null);
                        }}
                        icon={<TrashIcon />}
                      ></Button>
                    </Flex>
                  </Flex>
                </div>
              </Spin>
            ) : (
              <Button onClick={() => setShowMapModal(true)}>
                Добавить местоположение
              </Button>
            )}
          </div>

          <Form.Item
            name="unit_uuid"
            label="Рабочая группа"
            rules={[{ required: true, message: "Пожалуйста, заполните поле" }]}
          >
            <CustomSelect
              placeholder="Начните вводить"
              options={units}
              fieldNames={{
                label: "name",
                value: "uuid",
              }}
              showSearch
              optionFilterProp="name"
              allowClear={false}
              disabled={data?.uuid || !canEditUnit}
            />
          </Form.Item>

          <Form.Item name="sprint_uuid" label={sprintName}>
            <CustomSelect
              placeholder="Начните вводить"
              options={sprintsOptions}
              loading={isLoadingSprints}
              fieldNames={{
                label: "name",
                value: "uuid",
              }}
              showSearch
              optionFilterProp="name"
              allowClear
              disabled={!isUserCanEditSprint || !unitUuid}
            />
          </Form.Item>

          <Row gutter={{ xs: 10, sm: 10, md: 20, lg: 20 }}>
            <Col xs={24} sm={24}>
              <Row gutter={{ xs: 10, sm: 10, md: 20, lg: 20 }}>
                <Col xs={data?.uuid ? 8 : 12}>
                  <Form.Item name="start_date" label="Дата начала">
                    <DigitsDatePicker
                      disabled={!isUserCanEditStartDate}
                      style={{ width: "100%" }}
                      onChange={(date) => {
                        if (repeatRules) {
                          handleChangeDate(
                            "дату начала задачи",
                            "start_date",
                            date,
                            startDate,
                          );
                        } else {
                          form.setFieldValue("start_date", date);
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={data?.uuid ? 8 : 12}>
                  <Form.Item name="due_date" label="Срок исполнения">
                    <DigitsDatePicker
                      type="moment"
                      disabled={!isUserCanEditDueDate}
                      showTime={{ defaultValue: dayjs("18:00", "HH:mm") }}
                      style={{ width: "100%" }}
                      minDate={startDate}
                      onChange={(date) => {
                        if (repeatRules) {
                          handleChangeDate(
                            "срок исполнения задачи",
                            "due_date",
                            date,
                            dueDate,
                          );
                        } else {
                          form.setFieldValue("due_date", date);
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                {data?.uuid && (
                  <Col {...getPriorityColProps(data)}>
                    <Form.Item
                      name="importance_uuid"
                      label="Приоритет"
                      rules={[{ required: true, message: "Обязательное поле" }]}
                    >
                      <CustomSelect
                        showSearch
                        options={prioritiesOptions}
                        fieldNames={{ label: "name", value: "uuid" }}
                        optionFilterProp="name"
                        labelRender={renderPriorityTag(priorities)}
                        optionRender={renderPriorityOption}
                        disabled={!isUserCanEditPriority}
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>
              <Row gutter={{ xs: 10, sm: 10, md: 20, lg: 20 }}>
                <Col xs={24}>
                  <Flex gap={10} align="stretch">
                    {!data?.uuid && (
                      <div style={{ marginTop: "-18px", marginBottom: 20 }}>
                        {repeatRules ? (
                          <div
                            style={{
                              border: "1px solid var(--color-gray)",
                              borderRadius: 4,
                              padding: 12,
                            }}
                          >
                            <Flex vertical gap={12}>
                              <Flex vertical gap={6}>
                                <div style={{ color: "#777777" }}>
                                  Результат
                                </div>
                                <div>{getRepeatText(repeatRules)}</div>
                              </Flex>
                              <Flex gap={6}>
                                <Button
                                  onClick={() => setShowRepeatModal(true)}
                                >
                                  Изменить
                                </Button>

                                <Button
                                  onClick={() => {
                                    setRepeatRules(null);
                                    setTempRepeatRules(null);
                                  }}
                                  icon={<TrashIcon />}
                                />
                              </Flex>
                            </Flex>
                          </div>
                        ) : (
                          <Button
                            onClick={() => setShowRepeatModal(true)}
                            disabled={!startDate || isParentDraft}
                          >
                            Добавить повторение
                          </Button>
                        )}
                      </div>
                    )}

                    {/* <div style={{ marginTop: "-18px", marginBottom: 20, width: "100%" }}>
                      {plannedLaborCosts?.planned_costs || plannedLaborCosts?.require_actuals_on_acceptance ? (
                        <div
                          style={{
                            border: "1px solid var(--color-gray)",
                            borderRadius: 4,
                            padding: 12,
                          }}
                        >
                          <Flex vertical gap={12}>
                            <Flex vertical gap={6}>
                              <div style={{ color: "#777777" }}>
                                Планируемые трудозатраты
                              </div>
                              <div>{`${
                                plannedLaborCosts.planned_costs ||
                                "Не указано количество"
                              } ${getPluralizedWord(
                                toFloorNumber(plannedLaborCosts.planned_costs),
                                ["час", "часа", "часов"]
                              )}`}</div>
                              {plannedLaborCosts.require_actuals_on_acceptance && (
                                <div>
                                  Обязательное заполнение фактических
                                  трудозатрат
                                </div>
                              )}
                              {plannedLaborCosts.do_not_exceed_children && (
                                <div>
                                  Запрет превышения плановых затрат в дочерних задачах
                                </div>
                              )}
                            </Flex>
                            <Flex gap={6}>
                              <Button
                                onClick={() =>
                                  setShowPlannedLaborCostsModal(true)
                                }
                              >
                                Изменить
                              </Button>

                              <Button
                                onClick={() => {
                                  setPlannedLaborCosts(null);
                                }}
                                icon={<TrashIcon />}
                              />
                            </Flex>
                          </Flex>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setShowPlannedLaborCostsModal(true)}
                        >
                          Добавить трудозатраты
                        </Button>
                      )}
                    </div> */}
                  </Flex>
                </Col>
              </Row>
            </Col>

            {!data?.uuid && (
              <Col {...getPriorityColProps(data)}>
                <Form.Item
                  name="importance_uuid"
                  label="Приоритет"
                  rules={[{ required: true, message: "Обязательное поле" }]}
                >
                  <CustomSelect
                    showSearch
                    options={prioritiesOptions}
                    fieldNames={{ label: "name", value: "uuid" }}
                    optionFilterProp="name"
                    labelRender={renderPriorityTag(priorities)}
                    optionRender={renderPriorityOption}
                    disabled={!isUserCanEditPriority}
                  />
                </Form.Item>
              </Col>
            )}

            {data?.uuid && data?.status_uuid !== draftStatus?.uuid && (
              <Col xs={{ span: 12, push: 0 }} sm={{ span: 8, push: 16 }}>
                <Form.Item
                  name="status_uuid"
                  label="Статус"
                  rules={[{ required: true, message: "Обязательное поле" }]}
                >
                  <CustomSelect
                    showSearch
                    options={statusesOptions}
                    fieldNames={{ label: "name", value: "uuid" }}
                    optionFilterProp="name"
                    labelRender={renderStatusTag(statuses)}
                    optionRender={renderStatusOption}
                    disabled={!isUserCanEditStatus}
                  />
                </Form.Item>
              </Col>
            )}

            <Col {...getExecutorColProps(data, draftStatus)}>
              <AssignTo showAction={showSetMeExecutor} onChange={setMeExecutor}>
                <Form.Item name="responsible_executor_uuid" label="Исполнитель">
                  <CustomSelect
                    placeholder="Начните вводить"
                    options={executorsOptions}
                    showSearch
                    allowClear
                    fieldNames={{ value: "uuid" }}
                    optionFilterProp="label"
                    onChange={(value) => {
                      form.setFieldsValue({
                        responsible_executor_uuid: value || null, // Устанавливаем null, если значение очищено
                      });
                    }}
                    disabled={!isUserCanEditExecutor}
                  />
                </Form.Item>
              </AssignTo>
            </Col>

            <Col xs={24}>
              <Form.Item name="coexecutors_uuids" label="Соисполнители">
                <CustomSelect
                  placeholder="Начните вводить"
                  options={coexecutorsOptions}
                  mode="multiple"
                  showSearch
                  allowClear
                  fieldNames={{ value: "uuid" }}
                  optionFilterProp="label"
                  disabled={!isUserCanEditCoexecutors}
                />
              </Form.Item>
            </Col>

            <Col xs={24}>
              <AssignTo showAction={showSetMeAcceptor} onChange={setMeAcceptor}>
                <Form.Item name="assignee_acceptor_uuid" label="Приемщик">
                  <CustomSelect
                    placeholder="Начните вводить"
                    options={acceptorsOptions}
                    showSearch
                    allowClear
                    fieldNames={{ value: "uuid" }}
                    optionFilterProp="label"
                    onChange={(value) => {
                      form.setFieldsValue({
                        assignee_acceptor_uuid: value || null, // Устанавливаем null, если значение очищено
                      });
                    }}
                    disabled={!isUserCanEditAcceptor}
                  />
                </Form.Item>
              </AssignTo>
            </Col>
          </Row>

          <Row>
            <Col>
              <Form.Item
                name="is_accessible_for_all_in_unit"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Checkbox>Уведомлять всех в рабочей группе</Checkbox>
              </Form.Item>

              {data?.uuid && (
                <Form.Item
                  name="is_create_new_template"
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <Checkbox>Создать шаблон на основе текущей задачи</Checkbox>
                </Form.Item>
              )}
            </Col>
          </Row>

          {isParentDraft && (
            <Text type="secondary">
              Родительская задача находится в&nbsp;статусе черновик. Новая
              подзадача может быть сохранена только как&nbsp;черновик.
            </Text>
          )}

          {error && <Text type="danger">{error}</Text>}
        </Form>
      </Modal>

      {isPublishModalShow && (
        <PublishModal
          data={publishTask}
          statuses={statuses}
          visible={isPublishModalShow}
          onSave={handlePublishSave}
          onCancel={() => setIsPublishModalShow(false)}
        />
      )}

      {showRepeatModal && (
        <RepeatModal
          task={form.getFieldsValue()}
          data={repeatRules || tempRepeatRules || null}
          visible={showRepeatModal}
          onCancel={(data) => {
            setTempRepeatRules(data);
            setShowRepeatModal(false);
          }}
          onSave={(data) => {
            setRepeatRules(data);
            setShowRepeatModal(false);
          }}
        />
      )}

      {/* {showPlannedLaborCostsModal && (
        <PlannedLaborCostsModal
          data={plannedLaborCosts || tempPlannedLaborCosts || null}
          visible={showPlannedLaborCostsModal}
          parentDoNotExceedChildren={parentDoNotExceedChildren}
          plannedUpLimit={plannedUpLimit}
          plannedLowLimit={plannedLowLimit}
          onCancel={(data) => {
            setTempPlannedLaborCosts(data);
            setShowPlannedLaborCostsModal(false);
          }}
          onSave={(data) => {
            setTempPlannedLaborCosts(data);
            setPlannedLaborCosts(data);
            setShowPlannedLaborCostsModal(false);
          }}
        />
      )} */}

      {showActualLaborCostsModal && (
        <ActualLaborCostsModal
          data={null}
          task_uuid={data?.uuid}
          unit_uuid={data?.unit_uuid}
          visible={showActualLaborCostsModal}
          onCancel={() => setShowActualLaborCostsModal(false)}
          onSave={onLaborCostSave}
          isAdmins={isAdmins}
          text="Обязательно заполнение фактических трудозатрат. Перед сохранением задачи заполните трудозатраты"
        />
      )}

      {showMapModal && (
        <MapModal
          data={location}
          visible={showMapModal}
          onCancel={() => setShowMapModal(false)}
          onSave={(loc) => {
            setLocation(loc);
            setShowMapModal(false);
          }}
        />
      )}
    </>
  );
};

export default TaskModal;
